<template>
  <section class="activity-stage mathMaster">
    <div id="squares" :class="{ withoutAnswer: withoutAnswer }">
      <square
        v-for="(number, index) in result.numbers"
        track-by="$index"
        :key="`number${index}`"
        :class="[{ answer: index === 2 }]"
        :number="number === 0 ? 'zero' : number"
      ></square>
    </div>
    <div id="expression">
      <blinking-cursor-text
        :class="['blink-text', { answer: index === 4 }]"
        v-for="(step, index) in result.steps"
        :key="`step${index}`"
        :text="step.answer"
        :text-step="index"
      ></blinking-cursor-text>
    </div>
    <div
      id="results"
      :style="{ marginTop: usingExternalKeyboard && usingMobileChrome ? '50px' : '140px' }"
      v-show="chooseResult"
    >
      <div
        v-for="(result, index) in currentRound.results"
        :key="`result${index}`"
        track-by="$index"
        class="result"
        :id="`res${index}`"
        @click="isCorrectAnswer(result)"
      >
        <square :number="result.number === 0 ? 'zero' : result.number"></square>
        <p v-text="result.text"></p>
      </div>
    </div>
    <slot name="keyboard"></slot>
  </section>
</template>

<script>
import { TweenMax, TimelineLite } from 'gsap';

import AssetLoader from 'mixins/AssetLoader';
import ErrorLogger from 'mixins/ErrorLogger';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';

import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';

import Square from 'components/activity/MathMasters/Square.vue';

export default {
  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, ErrorLogger],

  components: {
    Square,
    BlinkingCursorText
  },

  data() {
    return {
      activityName: 'Math Masters',
      result: {},
      chooseResult: false,
      withoutAnswer: true
    };
  },

  methods: {
    getActivityState() {
      const { rounds } = this.activityData;

      return {
        rounds,
        instructions: {
          written: 'Type the math sentence. Then select and type the answer.',
          voice: `${this.voiceInstructionsPrefix}/mathmasters-${this.getRandomVoice()}-1.mp3`,
          delay: 5
        }
      };
    },

    async loadActivity() {
      const roundSetup = this.getActivityState();
      this.createRounds(roundSetup);
    },

    getKeyboardConfig() {
      const { keyboardType, roofs } = this.activityData.keyboard;
      return {
        keyboardType,
        hideNumberRow: false,
        roofs
      };
    },

    startActivity() {
      this.errorLoggerStart();
      this.setRound();
    },

    setRound() {
      this.setupNewRound();
      this.enableKeyboard();

      this.$nextTick(() => {
        this.result = {
          numbers: this.currentRound.numbers.slice(0, 2),
          steps: this.currentRound.steps.slice(0, 4)
        };
        this.withoutAnswer = true;
        this.chooseResult = false;
        TweenMax.to('.activity-stage', 0.5, { opacity: 1 });
      });
    },

    handleCorrectKey() {
      this.incrementTypingIndex();
      this.sndType();

      if (this.progress.stepIndex === 3) {
        this.hideKeyboard();

        TweenMax.delayedCall(0.8, () => {
          this.chooseResult = true;
          TweenMax.staggerFrom('.result', 0.8, { y: '130%' }, 0.5);
        });
      }

      if (this.isEndOfCurrentSentence) {
        this.setCurrentStep();
      }

      if (this.isEndOfRound) {
        this.sndBell();
        this.handleRoundEnd();
      }
    },

    handleRoundEnd() {
      const t = new TimelineLite();

      t.delay(0.8).call(() => {
        if (this.isLastRound) {
          TweenMax.delayedCall(0.8, this.completeActivity);
        } else {
          t.delay(1)
            .to('.activity-stage', 0.8, { opacity: 0 })
            .call(this.setRound);
        }
      });
    },

    isCorrectAnswer(result) {
      if (result.isCorrect) {
        this.result = {
          numbers: this.currentRound.numbers,
          steps: this.currentRound.steps
        };
        this.withoutAnswer = false;
        this.enableKeyboard();
        this.showKeyboard();
      } else return this.sndError();

      return this.sndBell();
    }
  },

  watch: {
    result() {
      TweenMax.to('.answer', 0, { autoAlpha: 0 });
      TweenMax.to('.answer', 0.8, { autoAlpha: 1 });
    }
  }
};
</script>

<style lang="scss">
.mathMaster {
  #expression {
    text-align: center;
    .blink-text {
      display: inline-block;
      font-size: 2.5vw;
      margin: 1% 3%;
      padding: 0;
      text-align: center;
      width: 80px;
    }
  }
  #results,
  #squares {
    text-align: center;
    padding: 0;
    &.withoutAnswer {
      margin-left: -14%;
    }
    .square {
      margin: 0 3%;
    }
    .result {
      display: inline-block;
      margin: 0 1%;
      p {
        font-size: 2.5vw;
        margin: 6.5% 0;
      }
    }
  }
  #squares {
    margin-top: 5%;
  }
}
</style>
